/**
 * Explicit populate maps for the Strapi queries.
 *
 * They replace `populate=deep,5` / `populate=deep,7`, which walked every
 * relation to the given depth and pulled in whole entities we never render -
 * most of all the `page` relations behind buttons and features, which brought
 * back the full target page (its own contentSections included) just so we could
 * read a slug.
 *
 * Two things to keep in mind when editing:
 *  - `populate` keys that do not exist on a content type are ignored by Strapi,
 *    so one map can cover every component in a dynamic zone. `fields` is not
 *    forgiving: an unknown attribute name makes the whole request fail with
 *    400, so only list attributes that really exist on that content type.
 *  - a field that is not listed here simply does not reach the components. When
 *    a new field is added in the CMS, it has to be added here too.
 */

/** Media attributes read by the components (`formats` and friends are not used). */
const MEDIA = {
  fields: ["url", "name", "alternativeText", "width", "height", "mime"],
};

/** Media used as a downloadable document - the size and extension are displayed. */
const FILE = {
  fields: ["url", "name", "alternativeText", "mime", "ext", "size"],
};

/**
 * A relation to a page is only used to build its URL
 * (`/{page_category.path}/{slug}`), never to render its content.
 */
const PAGE_LINK = {
  fields: ["slug"],
  populate: { page_category: { fields: ["path"] } },
};

/** Button / CTA component: scalars plus the page it may link to. */
const LINK = { populate: { page: PAGE_LINK } };

/** Steps of the contact form are linked by key, not by content. */
const STEP_ID = { fields: ["key"] };

/** A form field with its options and validation rules. */
const FORM_FIELDS = {
  populate: {
    nextStepId: STEP_ID,
    validations: { fields: ["type", "message"] },
    options: {
      populate: {
        image: MEDIA,
        nextStepId: STEP_ID,
        // Only the ids are sent back on submit.
        emailRecipient: { fields: ["id"] },
      },
    },
  },
};

/**
 * Article as rendered in a card (`sections.articles`, related articles):
 * title, date, thumbnail and the URL. Not the body, nor the SEO block.
 */
export const ARTICLE_CARD = {
  fields: [
    "title",
    "description",
    "slug",
    "createdAt",
    "publish_date",
    "hidePublishedDate",
  ],
  populate: {
    thumbnail: MEDIA,
    category: { fields: ["slug", "name"] },
  },
};

/**
 * Every component of the `contentSections` dynamic zone, in one map: Strapi
 * applies it to each component and ignores what does not fit.
 */
export const SECTIONS_POPULATE = {
  // sections.hero
  picture: MEDIA,
  mobilePicture: MEDIA,
  buttons: LINK,
  // sections.slider
  hero: {
    populate: { picture: MEDIA, mobilePicture: MEDIA, buttons: LINK },
  },
  // sections.heading
  columns: true,
  // sections.benefits-group
  benefit: { populate: { icon: MEDIA } },
  // sections.features
  feature: { populate: { media: MEDIA, page: PAGE_LINK } },
  // sections.products-group
  products: {
    populate: { images: MEDIA, productFeatures: true, button: LINK },
  },
  // sections.collapsible-group
  collapsibleItem: { populate: { richText: true } },
  faqs: {
    fields: ["title", "richText", "opened"],
    // The category is small and its fields differ between environments
    // (`description` does not exist on every one of them), so it is populated
    // whole rather than field by field.
    populate: { cta: LINK, faq_category: true },
  },
  // sections.articles
  articles: ARTICLE_CARD,
  button: LINK,
  // sections.team-members-group
  TeamMember: { populate: { image: MEDIA } },
  // sections.large-video
  video: MEDIA,
  poster: MEDIA,
  // sections.contact-form
  steps: {
    populate: {
      stepId: STEP_ID,
      sections: { populate: { fields: FORM_FIELDS } },
    },
  },
  // sections.form
  fields: FORM_FIELDS,
  // sections.documents-section
  categories: {
    populate: { documents: { populate: { file: FILE, cta: true } } },
  },
  // sections.lead-form
  submitButton: true,
  // sections.testimonials-group
  testimonials: { populate: { picture: MEDIA } },
  // sections.pricing
  plans: { populate: { product_features: { fields: ["name"] } } },
};

/** `/pages`: the sections that get rendered plus the SEO block. */
export const PAGE_POPULATE = {
  contentSections: { populate: SECTIONS_POPULATE },
  seo: { populate: { shareImage: MEDIA } },
};

/** `/articles` on the article page: cover, body blocks and SEO. */
export const ARTICLE_POPULATE = {
  cover: MEDIA,
  thumbnail: MEDIA,
  category: { fields: ["slug", "name"] },
  blocks: {
    populate: {
      // shared.media
      file: MEDIA,
      // shared.slider
      files: MEDIA,
    },
  },
  seo: { populate: { shareImage: MEDIA } },
};

/**
 * `/articles` filtered by category: the listing renders the hero of the
 * category and the articles as cards.
 */
export const ARTICLES_BY_CATEGORY_QUERY = {
  fields: ARTICLE_CARD.fields,
  populate: {
    ...ARTICLE_CARD.populate,
    category: {
      fields: ["slug", "name", "forcePageRedirect"],
      populate: {
        hero: {
          populate: { picture: MEDIA, mobilePicture: MEDIA, buttons: LINK },
        },
      },
    },
  },
};

/** Singletons behind ShowHero / ShowSolutions / ShowArticles. */
export const MOBILE_APP_POPULATE = {
  hero: { populate: { picture: MEDIA, mobilePicture: MEDIA, buttons: LINK } },
};

export const SOLUTION_POPULATE = {
  features: { populate: { feature: { populate: { media: MEDIA, page: PAGE_LINK } } } },
};

export const RELATED_ARTICLE_POPULATE = {
  buttons: LINK,
};
